<script setup>
import { ref, watch, onMounted } from 'vue';
import { useTimeAgo } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import ReportMetricCard from './ReportMetricCard.vue';
import ReportsAPI from 'dashboard/api/reports';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const conversationCount = ref('0');
const messageCount = ref('0');
const resolutionRate = ref('0');
const handoffRate = ref('0');
const lastUpdatedAt = ref(null);

const conversationCountTrend = ref(null);
const messageCountTrend = ref(null);
const resolutionRateTrend = ref(null);
const handoffRateTrend = ref(null);

const timeAgo = useTimeAgo(lastUpdatedAt);

const formatToPercent = value => {
  return value ? `${value}%` : '--';
};

const calculateTrend = (current, previous) => {
  if (!previous || previous === 0) return null;
  return ((current - previous) / previous) * 100;
};

const previousPeriodFilters = () => {
  const { from, to } = props.filters;
  const duration = to - from;
  return { from: from - duration, to: from };
};

const fetchMetrics = async () => {
  if (!props.filters.to || !props.filters.from) {
    return;
  }

  try {
    const [currentResponse, previousResponse] = await Promise.all([
      ReportsAPI.getBotMetrics(props.filters),
      ReportsAPI.getBotMetrics(previousPeriodFilters()),
    ]);

    const current = currentResponse.data;
    const previous = previousResponse.data;

    conversationCount.value = current.conversation_count.toLocaleString();
    messageCount.value = current.message_count.toLocaleString();
    resolutionRate.value = current.resolution_rate.toString();
    handoffRate.value = current.handoff_rate.toString();

    conversationCountTrend.value = calculateTrend(
      current.conversation_count,
      previous.conversation_count
    );
    messageCountTrend.value = calculateTrend(
      current.message_count,
      previous.message_count
    );
    resolutionRateTrend.value = calculateTrend(
      current.resolution_rate,
      previous.resolution_rate
    );
    handoffRateTrend.value = calculateTrend(
      current.handoff_rate,
      previous.handoff_rate
    );

    lastUpdatedAt.value = new Date();
  } catch {
    // Silently handle -- auto-refresh will retry
  }
};

const { startRefetching, stopRefetching } = useLiveRefresh(fetchMetrics);

watch(
  () => props.filters,
  () => {
    fetchMetrics();
    stopRefetching();
    startRefetching();
  },
  { deep: true }
);

onMounted(() => {
  fetchMetrics();
  startRefetching();
});
</script>

<template>
  <div
    class="flex flex-col mx-0 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
  >
    <div class="flex flex-wrap">
      <ReportMetricCard
        :label="t('BOT_REPORTS.METRIC.TOTAL_CONVERSATIONS.LABEL')"
        :info-text="t('BOT_REPORTS.METRIC.TOTAL_CONVERSATIONS.TOOLTIP')"
        :value="conversationCount"
        :trend="conversationCountTrend"
        class="flex-1"
      />
      <ReportMetricCard
        :label="t('BOT_REPORTS.METRIC.TOTAL_RESPONSES.LABEL')"
        :info-text="t('BOT_REPORTS.METRIC.TOTAL_RESPONSES.TOOLTIP')"
        :value="messageCount"
        :trend="messageCountTrend"
        class="flex-1"
      />
      <ReportMetricCard
        :label="t('BOT_REPORTS.METRIC.RESOLUTION_RATE.LABEL')"
        :info-text="t('BOT_REPORTS.METRIC.RESOLUTION_RATE.TOOLTIP')"
        :value="formatToPercent(resolutionRate)"
        :trend="resolutionRateTrend"
        class="flex-1"
      />
      <ReportMetricCard
        :label="t('BOT_REPORTS.METRIC.HANDOFF_RATE.LABEL')"
        :info-text="t('BOT_REPORTS.METRIC.HANDOFF_RATE.TOOLTIP')"
        :value="formatToPercent(handoffRate)"
        :trend="handoffRateTrend"
        invert-trend
        class="flex-1"
      />
    </div>
    <div
      v-if="lastUpdatedAt"
      class="flex items-center justify-end gap-1 pt-3 mt-3 border-t border-n-container"
    >
      <fluent-icon icon="arrow-sync" size="12" class="text-n-slate-10" />
      <span class="text-xxs text-n-slate-10">
        {{ t('BOT_REPORTS.LAST_UPDATED', { time: timeAgo }) }}
      </span>
    </div>
  </div>
</template>
